import React, { useEffect, useState } from 'react';
import { useRecordContext } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import {
    IconButton as MuiIconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List as MuiList,
    ListItem,
    ListItemText,
    Chip,
    Box,
    Typography,
    Link,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom';

import { API_URL } from '../../../services/settings';


const CampaignsDialog = ({ open, onClose, campaigns, onAddClick }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
            Campaigns ({campaigns.length})
        </DialogTitle>
        <DialogContent>
            {campaigns.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    This lead is not in any campaigns
                </Typography>
            ) : (
                <MuiList>
                    {campaigns.map((campaign) => (
                        <ListItem key={campaign.id} sx={{ py: 1 }}>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <span>{campaign.name}</span>
                                        <Chip
                                            label={campaign.campaign_status}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                }
                                secondary={
                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                        <Chip
                                            label={`Send: ${campaign.send_method}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                        <Chip
                                            label={`Status: ${campaign.lead_status}`}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </Box>
                                }
                            />
                        </ListItem>
                    ))}
                </MuiList>
            )}
        </DialogContent>
        <DialogActions>
            <Button onClick={onAddClick}>Add to Campaign</Button>
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
);

const AddToCampaignDialog = ({ open, onClose, campaigns, onAdd, loading }) => {
    const [selectedId, setSelectedId] = useState(null)
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add to Campaign</DialogTitle>
            <DialogContent>
                {campaigns.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No campaigns available
                    </Typography>
                ) : (
                    <Box sx={{ mt: 2 }}>
                        <select
                            style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                            }}
                            value={selectedId || ''}
                            onChange={(e) => setSelectedId(parseInt(e.target.value))}
                        >
                            <option value="">Select campaign...</option>
                            {campaigns.map((c) => (
                                <option key={c.id} value={c.id}>{c.name} ({c.status})</option>
                            ))}
                        </select>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={() => onAdd(selectedId)} disabled={!selectedId || loading}>
                    {loading ? 'Adding...' : 'Add'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

const CampaignsField = () => {
    const record = useRecordContext()
    const [campaigns, setCampaigns] = useState([]);
    const [available, setAvailable] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [addDialogOpen, setAddDialogOpen] = useState(false);

    // Load campaigns from API
    const loadCampaigns = React.useCallback(async () => {
        if (!record || !record.id) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/leads/${record.id}/campaigns`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setCampaigns(data.data || []);
            }
        } catch (e) {
            console.error('Error fetching campaigns:', e);
        }
    }, [record]);

    // Load campaigns on mount or when record changes
    useEffect(() => {
        loadCampaigns();
    }, [loadCampaigns]);

    if (!record || !record.id) {
        return null;
    }

    const handleOpenAddDialog = async () => {
        setLoading(true)
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/campaigns?page_size=100`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                const campaignIds = campaigns.map(c => c.id);
                const avail = (data.results || []).filter(c => !campaignIds.includes(c.id));
                setAvailable(avail);
                setAddDialogOpen(true);
            }
        } catch (e) {
            console.error('Error:', e);
        } finally {
            setLoading(false);
        }
    }

    const handleAdd = async (campaignId) => {
        if (!campaignId) return
        setLoading(true)
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/campaigns/${campaignId}/leads`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ lead_id: record.id }),
            });
            if (response.ok) {
                setAddDialogOpen(false);
                // Reload campaigns to update the counter
                await loadCampaigns();
            }
        } catch (e) {
            console.error('Error:', e);
        } finally {
            setLoading(false);
        }
    }

    const lastCampaign = campaigns.length > 0 ? campaigns[campaigns.length - 1] : null;
    const statusColors = {
        pending: 'default',
        scheduled: 'info',
        sent: 'primary',
        opened: 'success',
        replied: 'success',
        bounced: 'error',
        failed: 'error',
    };

    return (
        <>
            {campaigns.length === 0 ? (
                <MuiIconButton
                    size="small"
                    onClick={handleOpenAddDialog}
                    disabled={loading}
                    title="Add to campaign"
                    sx={{ color: 'action.disabled' }}
                >
                    <AddIcon fontSize="small" />
                </MuiIconButton>
            ) : (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Link
                            component={RouterLink}
                            to={`/campaigns/${lastCampaign.id}`}
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                        >
                            {lastCampaign.name}
                        </Link>
                        <Chip
                            label={lastCampaign.lead_status || 'pending'}
                            size="small"
                            color={statusColors[lastCampaign.lead_status] || 'default'}
                            variant="outlined"
                        />
                    </Box>
                    {campaigns.length > 1 && (
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            (+{campaigns.length - 1})
                        </Typography>
                    )}
                    <MuiIconButton
                        size="small"
                        onClick={handleOpenAddDialog}
                        disabled={loading}
                        title="Add to another campaign"
                        sx={{ color: 'primary.main' }}
                    >
                        <AddIcon fontSize="small" />
                    </MuiIconButton>
                </Box>
            )}
            <CampaignsDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                campaigns={campaigns}
                onAddClick={() => {
                    setDialogOpen(false)
                    handleOpenAddDialog()
                }}
            />
            <AddToCampaignDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                campaigns={available}
                onAdd={handleAdd}
                loading={loading}
            />
        </>
    );
};

export default CampaignsField;
